import { create } from 'zustand';
import type { Expense } from '@/types';

interface ExpenseState {
  expenses: Expense[];
  addExpense: (expense: Partial<Expense>) => void;
  updateExpense: (id: string, updates: Partial<Expense>) => void;
  replaceAll: (expenses: Expense[]) => void;

  getByEntity: (entityId: string) => Expense[];
  getByStatus: (entityId: string, status: string) => Expense[];
}

// NS13 Risk #7 fix: initial state is empty; pages hydrate from /v1/expenses via api.ts.
// Previously seeded from mockExpenses which leaked real Aalpen PII (EIN 12-3456789, name,
// Section 105 + Solo 401(k) + IR specialty) into every new tenant's first page render.
export const useExpenseStore = create<ExpenseState>((set, get) => ({
  expenses: [],

  addExpense: (expense) => {
    const full: Expense = {
      id: expense.id ?? crypto.randomUUID(),
      entityId: expense.entityId ?? '',
      userId: expense.userId ?? '',
      vendor: expense.vendor ?? '',
      amount: expense.amount ?? 0,
      expenseDate: expense.expenseDate ?? new Date().toISOString().split('T')[0],
      category: expense.category ?? '',
      ircCode: expense.ircCode ?? '',
      ircDescription: expense.ircDescription ?? '',
      sourceVector: expense.sourceVector ?? 'manual',
      isTravelLocation: expense.isTravelLocation ?? false,
      complianceStatus: expense.complianceStatus ?? 'pending',
      deductibilityPct: expense.deductibilityPct ?? 100,
      workflowState: expense.workflowState ?? 'draft',
      createdAt: expense.createdAt ?? new Date().toISOString(),
      updatedAt: expense.updatedAt ?? new Date().toISOString(),
      ...expense,
    } as Expense;

    set((state) => ({ expenses: [full, ...state.expenses] }));
  },

  updateExpense: (id, updates) =>
    set((state) => ({
      expenses: state.expenses.map((e) =>
        e.id === id ? { ...e, ...updates, updatedAt: new Date().toISOString() } : e
      ),
    })),

  /** Replace all expenses immutably — used by dataset switcher */
  replaceAll: (newExpenses) => set({ expenses: [...newExpenses] }),

  getByEntity: (entityId) => get().expenses.filter((e) => e.entityId === entityId),

  getByStatus: (entityId, status) =>
    get().expenses.filter((e) => e.entityId === entityId && e.complianceStatus === status),
}));
